import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Upload as UploadIcon } from "@mui/icons-material";
import { bankStatementService } from "../services/bankStatementService";

interface BankStatementUploadProps {
  onUploadSuccess: () => void;
}

export const BankStatementUpload: React.FC<BankStatementUploadProps> = ({
  onUploadSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [mes, setMes] = useState<number>(new Date().getMonth() + 1);
  const [ano, setAno] = useState<number>(new Date().getFullYear());
  const [banco, setBanco] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Por favor selecione um ficheiro PDF");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await bankStatementService.uploadStatement(file, {
        mes,
        ano,
        banco: banco || undefined,
      });

      setSuccess(true);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById(
        "pdf-upload"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }

      onUploadSuccess();
    } catch (err: any) {
      setError(err.message || "Erro ao processar extrato");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Carregar Extrato Bancário
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(false)}
        >
          Extrato processado com sucesso!
        </Alert>
      )}

      <Stack spacing={2}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadIcon />}
          fullWidth
        >
          {file ? file.name : "Selecionar PDF"}
          <input
            id="pdf-upload"
            type="file"
            accept="application/pdf"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            fullWidth
            type="number"
            label="Mês"
            value={mes}
            onChange={(e) => setMes(parseInt(e.target.value))}
            inputProps={{ min: 1, max: 12 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Ano"
            value={ano}
            onChange={(e) => setAno(parseInt(e.target.value))}
            inputProps={{ min: 2000, max: 2100 }}
          />

          <TextField
            fullWidth
            label="Banco (opcional)"
            value={banco}
            onChange={(e) => setBanco(e.target.value)}
            placeholder="Ex: CGD, BCP..."
          />
        </Stack>

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : "Processar Extrato"}
        </Button>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 2, display: "block" }}
      >
        O sistema tentará extrair automaticamente as informações do PDF. Pode
        editar as transações após o processamento.
      </Typography>
    </Paper>
  );
};
